import React, { useState } from "react";
import { useBoardsList } from "../../../hooks/api/useBoardsList";
import Spinner from "../../Spinner";
import logger from "../../../lib/logger";
import { List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { Search, Checkbox } from "@vibe/core";
import { DropdownChevronLeft, Warning } from "@vibe/icons";
import { useSearchList } from "../../../hooks/ui";
import { BoardType } from "../../addItem/BoardsList";
import ButtonComponent from "../../Button";
import { MondayApiQueryInstance } from "../../../services/monday/monday-service-ts";
import useBoardAndItems from "../../../hooks/context/useBoardAndItems";
import simbApi from "../../../utils/requests";
import { ADD_COLUMN_TO_BOARD } from "../../../data/constants/endpoints";
import useTokenHook from "../../../hooks/context/useTokenHook";

const backend_uri = process.env.REACT_APP_URI;

interface AddColumnBoardsListBigProps {
  workspaceId: number | string;
  workspaceName: string;
  setActiveMenu: (menu: string) => void;
  originColumnTitle: string;
  originColumnType: string;
  originColumnId: string;
  originBoard: string;
}

const AddColumnBoardsListBig = ({
  workspaceId,
  workspaceName,
  setActiveMenu,
  originColumnTitle,
  originColumnType,
  originColumnId,
  originBoard,
}: AddColumnBoardsListBigProps) => {
  const [search, setSearch] = useState("");
  const [retry, setRetry] = useState<boolean>(false);
  const [selectedBoards, setSelectedBoards] = useState<number[]>([]);
  const [addColumnLoading, setAddColumnLoading] = useState(false);
  const [reauth, setReauth] = useState<number>(0);
  const { currentBoard } = useBoardAndItems();
  const { token } = useTokenHook();
  const { boardsData, isLoading, error, mutateBoardsList } = useBoardsList(workspaceId as number);
  const filteredItems = useSearchList<BoardType>(boardsData, search, "name");

  const retryFetch = async () => {
    try {
      setRetry(true);
      setReauth(reauth + 1);
      await mutateBoardsList();
    } catch (error: any) {
      logger.error("Error retrying fetch boards", error);
    } finally {
      setRetry(false);
    }
  };

  const handleToggleBoard = (boardId: number) => {
    setSelectedBoards((prevSelected) =>
      prevSelected.includes(boardId)
        ? prevSelected.filter((id) => id !== boardId)
        : [...prevSelected, boardId],
    );
  };

  const handleAddColumn = async () => {
    try {
      setAddColumnLoading(true);

      await simbApi.post(
        ADD_COLUMN_TO_BOARD,
        {
          targetBoardIds: selectedBoards,
          originColumnTitle: originColumnTitle,
          originColumnType: originColumnType,
          originColumnId: originColumnId,
          originBoard: originBoard,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      await MondayApiQueryInstance.mondayToastNotification("Column Added!", "success", 5000);
    } catch (error: any) {
      logger.error("Error adding columns to boards", error);

      await MondayApiQueryInstance.mondayToastNotification("Column didn't add!", "error", 5000);
    } finally {
      setAddColumnLoading(false);
    }
  };

  if (isLoading || retry) {
    return (
      <>
        <div className="px-2 pt-[15px]">
          <div className="flex gap-2 items-center">
            <Spinner /> Getting your boards
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {error ? (
        <div className="flex flex-col items-center justify-center gap-2 px-2 pt-[15px] secondary-menu-ref">
          {reauth >= 1 ? (
            <div className="flex items-center text-center justify-center max-w-[241px]">
              <span>
                If the error persists, try authorizing again using this{" "}
                <a
                  className="hover:underline"
                  rel="noreferrer"
                  href={`${backend_uri}/oauth/authorize?source=newtab`}
                  target="_blank"
                >
                  link
                </a>
              </span>
            </div>
          ) : (
            <>
              <div className="flex gap-2 justify-center items-center">
                <Warning />
                <span>An error occurred. Please try again.</span>
              </div>
            </>
          )}

          <div className="flex justify-center">
            <ButtonComponent text="Retry" className="retry-button" onClick={retryFetch} />
          </div>
        </div>
      ) : (
        <div className="flex flex-col h-full secondary-menu-ref">
          <>
            <div
              style={{ padding: "15px 8px 12px 8px" }}
              className="bg-[#FFFFFF] sticky top-0 z-50 border-b border-[#C3C6D4] overflow-x-hidden add-item-dropdown"
            >
              <div
                id="boards_list_back_button"
                onClick={() => setActiveMenu("main")}
                className="flex flex-row items-center gap-1 mb-2 cursor-pointer rounded hover:bg-[#6768791a] pr-2 pl-1 py-1"
              >
                <DropdownChevronLeft />
                <h4>{workspaceName}</h4>
              </div>
              <Search
                placeholder="Search Boards"
                onChange={(e) => {
                  setSearch(e);
                }}
                size="small"
                value={search}
              />
            </div>
            {/* Boards*/}
            <List>
              {boardsData.length > 0 &&
                filteredItems.map((board: BoardType) => {
                  return (
                    <div key={board.id}>
                      <ListItemButton
                        onClick={() => handleToggleBoard(board.id as number)}
                        sx={{
                          borderRadius: "4px", // Other styles here
                        }}
                        disabled={
                          addColumnLoading || parseInt(board.id as string) === currentBoard!
                        }
                      >
                        <ListItemIcon
                          sx={{ minWidth: "unset" }}
                          className="!w-auto mr-1 flex items-center"
                        >
                          <Checkbox
                            checked={selectedBoards.includes(board.id as number)}
                            disabled={
                              addColumnLoading || parseInt(board.id as string) === currentBoard!
                            }
                            onChange={() => {
                              handleToggleBoard(board.id as number);
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{
                            fontSize: "0.875rem",
                            fontFamily: "Poppins",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          primary={`${board.name}`}
                        />
                      </ListItemButton>
                    </div>
                  );
                })}
            </List>
          </>
          <div className="flex sticky bottom-0 z-50 justify-end p-3 w-full bg-white">
            <ButtonComponent
              text="Add Column"
              size="small"
              disabled={selectedBoards.length === 0}
              onClick={handleAddColumn}
              loading={addColumnLoading}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default AddColumnBoardsListBig;
